// One client: its details, its jobs, its representatives, its notes, and the
// archive switch.
//
// There was no way to edit a client at all until round 33 — the list offered
// only Archive, and `updateClient` had sat unused in the API client since the
// route was written. A typo in a client's name was permanent.
//
// Deliberately a PAGE rather than a dialog: a client now carries two contacts,
// two identifiers, a representatives list, a notes log and a jobs list, which
// is far more than a dialog holds comfortably at 375px, and a page gives the
// row something to link to.
//
// TABBED since round 35, taking the job page's shape. Round 34 stacked four
// panels down one column, and Nathan hit the cost on the second walkthrough:
// saving a note and scrolling put a scrollbar inside a panel beside the page's
// own, and the two scrolled to different extents. Tabs put each section on its
// own short screen, and the one nested scroll container (the reps table's
// card-in-card `.table-wrap`) is gone with it.
//
// WIDTH. The page caps itself at `max-w-3xl`, which is Zanetto Tools' own
// measure for the same screen. The app shell deliberately caps nothing (a
// table or a board should get every pixel a wide monitor offers), so a form
// page sets its own or it runs to 2000px of half-empty inputs — which is
// exactly what this screen did. No `mx-auto`: it stays left-aligned against
// the sidebar, like ZT's and like the non-working-days page.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  scheduling,
  type Client,
  type ClientNote,
  type ClientRep,
  type JobListItem,
} from '../schedulingApi';
import { ApiError } from '../api';
import { Banner, Field } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { CardList, RowCard } from '../components/ui/RowCard';
import { LinkedRow } from '../components/ui/LinkedRow';
import { SortHeader } from '../components/ui/SortHeader';
import { StatusBadge } from '../components/ui';
import { NotesPanel } from '../components/NotesPanel';
import { RepsPanel } from '../components/clients/RepsPanel';
import { useTabParam } from '../lib/useTabParam';
import { sortRows, useSort } from '../lib/sortRows';
import { formatDate } from '../lib/dates';
import { STATUS_LABEL } from './JobsPage';
import { ClientQuotesPanel } from '../components/clients/ClientQuotesPanel';

const CLIENT_TABS = [
  { key: 'details', label: 'Details' },
  // Jobs sits second: grouping and tracking jobs is what the client record
  // exists for (spec 04), so it outranks who to ring.
  { key: 'jobs', label: 'Jobs' },
  // Round 43. Drawn only while the company uses quotes (see `tabs` below).
  { key: 'quotes', label: 'Quotes' },
  { key: 'reps', label: 'Representatives' },
  { key: 'notes', label: 'Notes' },
] as const;

const CLIENT_TAB_KEYS = CLIENT_TABS.map((t) => t.key);
type ClientTabKey = (typeof CLIENT_TABS)[number]['key'];

export function ClientDetailPage() {
  const { id = '' } = useParams();
  // Every company has quotes since round 45, so every client has the tab.
  const tabs = CLIENT_TABS;
  const [client, setClient] = useState<Client | null>(null);
  const [jobCount, setJobCount] = useState(0);
  const [reps, setReps] = useState<ClientRep[]>([]);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useTabParam(CLIENT_TAB_KEYS);
  // Null until the Jobs tab is first opened — the rest of the page needs only
  // the count, which `getClient` already answers, so the rows are not fetched
  // until somebody asks to see them.
  const [jobs, setJobs] = useState<JobListItem[] | null>(null);
  const [jobsError, setJobsError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await scheduling.getClient(id);
      setClient(result.client);
      setJobCount(result.jobCount);
      setReps(result.reps);
      setNotes(result.notes);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load that client.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab !== 'jobs' || jobs !== null) return;
    scheduling.listJobs({ clientId: id }).then(
      (result) => {
        setJobs(result.jobs);
        setJobsError(null);
      },
      (err: unknown) => {
        setJobsError(err instanceof ApiError ? err.message : 'Could not load the jobs.');
      }
    );
  }, [tab, jobs, id]);

  if (loadError) return <Banner kind="error">{loadError}</Banner>;
  if (!client) return <p className="muted">Loading…</p>;

  // The counts on the tabs, in the person page's style: shown only when there
  // is something behind the label to count.
  const tabCount = (key: ClientTabKey): number => {
    if (key === 'jobs') return jobCount;
    if (key === 'reps') return reps.length;
    if (key === 'notes') return notes.length;
    return 0;
  };

  return (
    <>
      <PageHeader
        title={client.name}
        subtitle={jobCount === 1 ? '1 job' : `${jobCount} jobs`}
        actions={
          <Link to="/app/clients" className="btn-secondary">
            Back to clients
          </Link>
        }
      />
      <div className="max-w-3xl">
        <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
          {tabs.map((section) => {
            const active = tab === section.key;
            const count = tabCount(section.key);
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => setTab(section.key)}
                aria-current={active ? 'page' : undefined}
                className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                  active
                    ? 'border-brand-700 font-medium text-brand-800'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {section.label}
                {count > 0 && <span className="ml-1 text-xs text-gray-500">{count}</span>}
              </button>
            );
          })}
        </div>

        {tab === 'details' && (
          <>
            <ClientForm client={client} onSaved={load} />
            <ArchivePanel client={client} onChanged={load} />
          </>
        )}

        {tab === 'jobs' && <ClientJobsPanel jobs={jobs} error={jobsError} />}

        {tab === 'quotes' && <ClientQuotesPanel clientId={client.id} />}

        {tab === 'reps' && <RepsPanel clientId={client.id} reps={reps} onChanged={load} />}

        {tab === 'notes' && (
          <NotesPanel
            notes={notes}
            canWrite
            intro="For your team. A note records who wrote it and when."
            onAdd={async (body) => {
              const { note } = await scheduling.addClientNote(client.id, body);
              // Prepended rather than refetched: the list is newest-first and
              // the server has just handed back the row it saved.
              setNotes((current) => [note, ...current]);
            }}
          />
        )}
      </div>
    </>
  );
}

/**
 * The client's jobs (round 35) — every job filed against this client, each row
 * opening the job.
 *
 * The rows come from the same `GET /jobs` the list page uses, narrowed with
 * `clientId`, so this can never disagree with the main list about what a job
 * row says. No status sub-tabs: the jobs page already has those, and this is a
 * short scoped list, not a second jobs page.
 *
 * A tab answers even when it is empty (round 33's rule), so the empty state
 * renders inside the panel rather than the panel vanishing.
 */
function ClientJobsPanel({ jobs, error }: { jobs: JobListItem[] | null; error: string | null }) {
  type SortKey = 'number' | 'title' | 'status' | 'nextBooked';
  const { sortKey, sortDir, onSort } = useSort<SortKey>('number', 'desc');

  // One sorted array for both renderings — the phone cards and the desktop
  // table must never disagree about order (round 34's rule).
  const visible = useMemo(
    () =>
      sortRows(
        jobs ?? [],
        (job) => {
          switch (sortKey) {
            case 'number':
              return job.number;
            case 'title':
              return job.title;
            case 'status':
              return STATUS_LABEL[job.status];
            case 'nextBooked':
              return job.nextBookedDate;
          }
        },
        sortDir
      ),
    [jobs, sortKey, sortDir]
  );

  return (
    <section className="panel">
      <h2>Jobs</h2>
      {error ? (
        <Banner kind="error">{error}</Banner>
      ) : jobs === null ? (
        <p className="muted">Loading…</p>
      ) : jobs.length === 0 ? (
        <p className="muted">No jobs for this client yet.</p>
      ) : (
        <>
          <CardList>
            {visible.map((job) => (
              <RowCard
                key={job.id}
                to={`/app/jobs/${job.id}`}
                title={
                  <>
                    <span className="tabular-nums">{job.number}</span> · {job.title}
                  </>
                }
                badges={<StatusBadge status={job.status} kind="job" />}
              >
                {job.conflictCount > 0 && (
                  <div className="font-medium text-red-700">needs re-planning</div>
                )}
                <div>
                  {job.nextBookedDate
                    ? `Next booked ${formatDate(job.nextBookedDate)}`
                    : 'Not booked'}
                </div>
              </RowCard>
            ))}
          </CardList>
          <div className="-mx-6 overflow-x-auto border-y border-gray-200 max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Number" sortKey="number" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Title" sortKey="title" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Next booked" sortKey="nextBooked" current={sortKey} dir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {visible.map((job) => (
                  <LinkedRow key={job.id} to={`/app/jobs/${job.id}`}>
                    <td className="whitespace-nowrap font-medium tabular-nums">
                      <Link to={`/app/jobs/${job.id}`} className="table-link">
                        {job.number}
                      </Link>
                    </td>
                    <td>
                      {job.title}
                      {job.conflictCount > 0 && (
                        <span className="pill conflict-pill ml-2">needs re-planning</span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={job.status} kind="job" />
                    </td>
                    <td className="table-muted whitespace-nowrap">
                      {job.nextBookedDate ? formatDate(job.nextBookedDate) : 'not booked'}
                    </td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function ClientForm({ client, onSaved }: { client: Client; onSaved: () => Promise<void> }) {
  const [name, setName] = useState(client.name);
  const [contactPerson, setContactPerson] = useState(client.contactPerson ?? '');
  const [phone, setPhone] = useState(client.phone ?? '');
  const [email, setEmail] = useState(client.email ?? '');
  const [address, setAddress] = useState(client.address ?? '');
  const [accountsName, setAccountsName] = useState(client.accountsName ?? '');
  const [accountsPhone, setAccountsPhone] = useState(client.accountsPhone ?? '');
  const [accountsEmail, setAccountsEmail] = useState(client.accountsEmail ?? '');
  const [abn, setAbn] = useState(client.abn ?? '');
  const [acn, setAcn] = useState(client.acn ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const dirty =
    name !== client.name ||
    contactPerson !== (client.contactPerson ?? '') ||
    phone !== (client.phone ?? '') ||
    email !== (client.email ?? '') ||
    address !== (client.address ?? '') ||
    accountsName !== (client.accountsName ?? '') ||
    accountsPhone !== (client.accountsPhone ?? '') ||
    accountsEmail !== (client.accountsEmail ?? '') ||
    abn !== (client.abn ?? '') ||
    acn !== (client.acn ?? '');

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      await scheduling.updateClient(client.id, {
        name,
        contactPerson,
        phone,
        email,
        address,
        accountsName,
        accountsPhone,
        accountsEmail,
        abn,
        acn,
      });
      await onSaved();
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fields);
      } else {
        setError('Could not save that. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Client details</h2>
      <form onSubmit={save} noValidate aria-label="Client details">
        {error && <Banner kind="error">{error}</Banner>}
        {saved && !dirty && <Banner kind="ok">Saved.</Banner>}

        <Field label="Name" name="name" value={name} onChange={setName} error={fieldErrors.name} />

        {/* Two columns on anything wider than a phone: who to ring on the
            left, who to invoice on the right. They are different people at
            most companies, and putting them side by side is what makes that
            obvious — ZT's arrangement, and the reason this screen was asked
            for. Below `sm` the grid collapses and contact sits above
            accounts, which is the order somebody fills them in. */}
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contact details
            </h3>
            <Field
              label="Contact person"
              name="contactPerson"
              value={contactPerson}
              onChange={setContactPerson}
              error={fieldErrors.contactPerson}
              required={false}
            />
            <Field
              label="Phone"
              name="phone"
              value={phone}
              onChange={setPhone}
              error={fieldErrors.phone}
              required={false}
              inputMode="tel"
            />
            <Field
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={setEmail}
              error={fieldErrors.email}
              required={false}
              autoCapitalize="none"
              spellCheck={false}
              inputMode="email"
            />
          </div>

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Accounts
            </h3>
            <Field
              label="Accounts contact"
              name="accountsName"
              value={accountsName}
              onChange={setAccountsName}
              error={fieldErrors.accountsName}
              required={false}
            />
            <Field
              label="Accounts phone"
              name="accountsPhone"
              value={accountsPhone}
              onChange={setAccountsPhone}
              error={fieldErrors.accountsPhone}
              required={false}
              inputMode="tel"
            />
            <Field
              label="Accounts email"
              name="accountsEmail"
              value={accountsEmail}
              onChange={setAccountsEmail}
              error={fieldErrors.accountsEmail}
              required={false}
              type="email"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="email"
            />
          </div>
        </div>

        <div className="row">
          {/* Both check-digit validated on the server and stored formatted, so
              a transposed digit is caught here rather than turning up on
              somebody's paperwork. A sole trader has an ABN and no ACN, so
              neither is required. */}
          <Field
            label="ABN"
            name="abn"
            value={abn}
            onChange={setAbn}
            error={fieldErrors.abn}
            required={false}
            inputMode="numeric"
            autoCapitalize="none"
            spellCheck={false}
          />
          <Field
            label="ACN"
            name="acn"
            value={acn}
            onChange={setAcn}
            error={fieldErrors.acn}
            required={false}
            inputMode="numeric"
            autoCapitalize="none"
            spellCheck={false}
          />
        </div>

        <Field
          label="Address"
          name="address"
          value={address}
          onChange={setAddress}
          error={fieldErrors.address}
          required={false}
        />

        {/* Explicit class for the top margin: a submit following a `.row`
            has nothing spacing it, because `.row` strips its labels'
            bottom margin. Same reason as the Add client form. */}
        <button type="submit" className="btn-primary mt-4" disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </section>
  );
}

function ArchivePanel({ client, onChanged }: { client: Client; onChanged: () => Promise<void> }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function toggleArchive() {
    setBusy(true);
    setError(null);
    try {
      await scheduling.archiveClient(client.id, !client.archived);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>{client.archived ? 'Archived' : 'Archive this client'}</h2>
      {error && <Banner kind="error">{error}</Banner>}
      <p className="muted">
        {client.archived
          ? 'This client is archived. Their jobs are untouched; restoring them puts the client back in the list.'
          : 'Archiving keeps every job against this client and takes them out of the pickers. A client with jobs can be archived but never deleted.'}
      </p>
      <button type="button" className="btn-warning btn-sm" disabled={busy} onClick={toggleArchive}>
        {client.archived ? 'Restore client' : 'Archive client'}
      </button>
    </section>
  );
}
